import React, { useState, useRef, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { Pencil, Trash2, StickyNote, UserPlus, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { StatusBadge, ConfirmModal } from "../ui";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";

const EnquiryTableRow = ({ enquiry, onDelete, onNoteAdded, onConverted, selectable, selected, onSelect }) => {
  const navigate = useNavigate();
  const [showNoteBox, setShowNoteBox] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [showConvertConfirm, setShowConvertConfirm] = useState(false);
  const popRef = useRef(null);

  // Close popover on outside click
  useEffect(() => {
    if (!showNoteBox) return;
    const handler = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) {
        setShowNoteBox(false);
        setNoteText("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showNoteBox]);

  const handleSaveNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      const user = getCurrentUser();
      await apiClient.post(`/enquiries/${enquiry._id}/notes`, {
        note: noteText.trim(),
        createdBy: user?.name || "Admin",
      });
      setNoteText("");
      setShowNoteBox(false);
      if (onNoteAdded) onNoteAdded();
    } catch {
      // silent fail
    }
    setSaving(false);
  };

  const handleConvertToStudent = async () => {
    setShowConvertConfirm(false);
    setConverting(true);
    try {
      await apiClient.post(`/enquiries/${enquiry._id}/convert-to-student`);
      if (onConverted) onConverted();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to convert enquiry to student.";
      alert(msg);
    }
    setConverting(false);
  };

  const statusKey = (enquiry.status || "Enquired").toLowerCase();

  return (
    <tr>
      {selectable && (
        <td style={{ width: 36 }}>
          <input
            type="checkbox"
            checked={!!selected}
            onChange={(e) => onSelect(enquiry._id, e.target.checked)}
          />
        </td>
      )}
      <td className="eq-cell-id">{enquiry._id.slice(-6).toUpperCase()}</td>
      <td>
        <div
          className="eq-cell-name"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/enquiries/${enquiry._id}`)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate(`/enquiries/${enquiry._id}`);
            }
          }}
          style={{ cursor: "pointer" }}
          title="View enquiry"
        >
          <span className="eq-cell-name__full" style={{ color: "#2563eb" }}>{enquiry.fullName}</span>
          <span className="eq-cell-name__email">{enquiry.email}</span>
        </div>
      </td>
      <td>{enquiry.course?.name || enquiry?.otherCourse || "-"}</td>
      <td>{enquiry.mobile}</td>
      <td className="eq-cell-date">
        {enquiry.createdAt
          ? new Date(enquiry.createdAt).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })
          : "-"}
      </td>
      <td>
        <StatusBadge status={statusKey}>
          {enquiry.status || "Enquired"}
        </StatusBadge>
        {enquiry.zohoSyncStatus && (
          <span
            className={`badge ms-1 ${
              enquiry.zohoSyncStatus === "synced"
                ? "bg-success"
                : enquiry.zohoSyncStatus === "pending"
                ? "bg-warning text-dark"
                : enquiry.zohoSyncStatus === "failed"
                ? "bg-danger"
                : "bg-secondary"
            }`}
            title={`Zoho: ${enquiry.zohoSyncStatus}`}
            style={{ fontSize: "0.65rem", verticalAlign: "middle" }}
          >
            {enquiry.zohoSyncStatus === "synced" ? "Z✓" : enquiry.zohoSyncStatus === "pending" ? "Z⏳" : "Z✗"}
          </span>
        )}
      </td>
      <td className="eq-cell-assigned">
        {enquiry.assignedTo
          ? (
            <span className="eq-assigned-chip" title={enquiry.assignedTo.email}>
              {`${enquiry.assignedTo.firstName || ''} ${enquiry.assignedTo.lastName || ''}`.trim() || enquiry.assignedTo.email || '—'}
            </span>
          )
          : <span className="eq-assigned-chip eq-assigned-chip--none">—</span>
        }
      </td>
      <td className="eq-cell-notes">
        {(() => {
          const LABELS = {
            google_ads: "Google Ads",
            meta_ads: "Meta Ads",
            whatsapp: "WhatsApp",
            website: "Website",
            embed_form: "Embed Form",
            zoho_crm: "Zoho CRM",
            other: "Other",
            manual: "Manual",
          };
          const label =
            LABELS[enquiry?.leadSource] ||
            (enquiry?.otherCourse ? enquiry?.type : enquiry?.source) ||
            "-";
          const tip = enquiry?.campaign?.name
            ? `${label} · Campaign: ${enquiry.campaign.name}`
            : label;
          return (
            <span title={tip}>
              {label}
              {enquiry?.campaign?.name && (
                <span style={{ display: "block", fontSize: "0.7rem", color: "#64748b" }}>
                  {enquiry.campaign.name}
                </span>
              )}
            </span>
          );
        })()}
        {enquiry.followUpNotes?.length > 0 ? (
          <div className="eq-cell-notes__last">
            <strong>Last note:</strong>{" "}
            {enquiry.followUpNotes[enquiry.followUpNotes.length - 1]?.note?.slice(0, 50)}
            {enquiry.followUpNotes[enquiry.followUpNotes.length - 1]?.note?.length > 50 ? "…" : ""}
            <span className="eq-cell-notes__count">
              ({enquiry.followUpNotes.length})
            </span>
          </div>
        ) : (
          enquiry.comments && <div className="eq-cell-notes__comment">{enquiry.comments}</div>
        )}
      </td>
      <td className="text-end eq-cell-actions">
        {enquiry.status !== 'Converted' && (
          <button
            className="eq-action-btn eq-action-btn--success"
            title="Move to Student"
            onClick={() => setShowConvertConfirm(true)}
            disabled={converting}
          >
            <UserPlus size={15} />
          </button>
        )}
        <button
          className="eq-action-btn"
          title="View"
          onClick={() => navigate(`/enquiries/${enquiry._id}`)}
        >
          <Eye size={15} />
        </button>
        <button
          className="eq-action-btn"
          title="Add Note"
          onClick={() => setShowNoteBox((v) => !v)}
        >
          <StickyNote size={15} />
        </button>
        <button
          className="eq-action-btn"
          title="Edit"
          onClick={() => navigate(`/enquiries/edit/${enquiry._id}`)}
        >
          <Pencil size={15} />
        </button>
        <button
          className="eq-action-btn eq-action-btn--danger"
          title="Delete"
          onClick={() => onDelete(enquiry._id)}
        >
          <Trash2 size={15} />
        </button>

        {/* Quick-add note popover */}
        {showNoteBox && (
          <div className="eq-quick-note-pop" ref={popRef}>
            <div className="eq-quick-note-title">Add Follow-up Note</div>
            <textarea
              rows={2}
              placeholder="Write a note…"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              className="eq-quick-note-input"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSaveNote();
                }
              }}
            />
            <div className="eq-quick-note-actions">
              <button
                className="eq-note-btn eq-note-btn--cancel"
                onClick={() => { setShowNoteBox(false); setNoteText(""); }}
              >
                Cancel
              </button>
              <button
                className="eq-note-btn eq-note-btn--save"
                disabled={!noteText.trim() || saving}
                onClick={handleSaveNote}
              >
                {saving ? <Spinner size="sm" animation="border" /> : "Save"}
              </button>
            </div>
          </div>
        )}
      </td>

      {/* Convert to Student confirmation */}
      <ConfirmModal
        show={showConvertConfirm}
        onHide={() => setShowConvertConfirm(false)}
        onConfirm={handleConvertToStudent}
        title="Move to Student"
        message={<>Move <strong>{enquiry.fullName}</strong> to Students? This will create a new student record and mark this enquiry as Converted.</>}
        confirmText="Move to Student"
        cancelText="Cancel"
        variant="success"
        loading={converting}
      />
    </tr>
  );
};

export default EnquiryTableRow;
